import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import Header from '../../components/Header';
import StatusBadge from '../../components/StatusBadge';
import SearchableSelect from '../../components/SearchableSelect';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { aggregateOdemeler, splitOdemeler } from '../../utils/posOdeme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Lokasyon bazlı görünüm için hedef şubeler (imalathane ve pasif depolar hariç)
const TARGET_LOKASYONLAR = [
  { id: 3, ad: 'Inga Burger' },
  { id: 4, ad: 'Altı Üstü Köfte' },
  { id: 5, ad: 'Toruna Kasap' },
];

// Dün geriye 14 gün (bugün hariç) — "YYYY-MM-DD" string array, newest first
// LOCAL tarih kullan (toISOString UTC'ye çevirir, TR 00:00-02:59 arası
// yanlış gün üretir → backend rapor_tarihi ile eşleşmez).
const generateLast14Days = () => {
  const dates = [];
  const today = new Date();
  for (let i = 1; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    dates.push(`${yyyy}-${mm}-${dd}`);
  }
  return dates;
};

// Run array'ini lokasyon_id'ye göre grupla
const groupRunsByLokasyon = (runs) => {
  const grouped = {};
  runs.forEach(r => {
    if (!grouped[r.lokasyon_id]) grouped[r.lokasyon_id] = [];
    grouped[r.lokasyon_id].push(r);
  });
  return grouped;
};

// Belirli bir tarih için run bul
const findRunByDate = (runs, tarih) => {
  if (!runs) return null;
  return runs.find(r => r.rapor_tarihi === tarih) || null;
};

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};




const duzeltPosKarakterleri = (metin) => {
  const karakterler = {
    'ý': 'ı',
    'Ý': 'İ',
    'Þ': 'Ş',
    'Ð': 'Ğ',

  };

  return metin.replace(
    /[ýÝÞÐ]/g,
    karakter => karakterler[karakter]
  );
};


const POSSatisPage = () => {
  const { user } = useAuth();
  // "Maili Kontrol Et" backend'de require_permission(MANAGE_POS_EMAIL) ile korunur
  // (yönetim + admin). VIEW_POS_DATA'lı diğer roller (operasyon/sube/muhasebe)
  // butonu görmemeli — aksi halde tıklayınca 403 alır (M.5: frontend gating UX,
  // güvenlik backend'de).
  const canCheckEmail = !!user && hasPermission(user.role, PERMISSIONS.MANAGE_POS_EMAIL, user.effectivePermissions);
  const [view, setView] = useState('list'); // list, form, detail
  const toast = useToast();

  // Form state
  const [metin, setMetin] = useState('');
  const [lokasyonId, setLokasyonId] = useState('');
  // Gün sonu raporu her zaman önceki güne ait — varsayılan dün
  const [raporTarihi, setRaporTarihi] = useState(() => {
    const dun = new Date();
    dun.setDate(dun.getDate() - 1);
    return dun.toISOString().split('T')[0];
  });
  const [submitting, setSubmitting] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);

  // Data
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [receteler, setReceteler] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [runs, setRuns] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState({});

  // KPI: son rapor tarihi + bu haftanin cirosu (gunsonlari ertesi gun gelir)
  const sonRaporTarihi = useMemo(() => {
    if (runs.length === 0) return null;
    return runs.reduce((max, r) => r.rapor_tarihi > max ? r.rapor_tarihi : max, runs[0].rapor_tarihi);
  }, [runs]);

  const sonGunRuns = useMemo(() => {
    if (!sonRaporTarihi) return [];
    return runs.filter(r => r.rapor_tarihi === sonRaporTarihi);
  }, [runs, sonRaporTarihi]);

  const sonGunCiro = useMemo(() => {
    return sonGunRuns.reduce((sum, r) => sum + (r.net_ciro != null ? r.net_ciro : (r.toplam_ciro || 0)), 0);
  }, [sonGunRuns]);

  const haftalikCiro = useMemo(() => {
    const now = new Date();
    const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).toISOString().split('T')[0];
    return runs
      .filter(r => r.rapor_tarihi >= weekAgo)
      .reduce((sum, r) => sum + (r.net_ciro != null ? r.net_ciro : (r.toplam_ciro || 0)), 0);
  }, [runs]);

  const sonGunTarihLabel = useMemo(() => {
    if (!sonRaporTarihi) return '-';
    const d = new Date(sonRaporTarihi + 'T00:00:00');
    return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', weekday: 'short' });
  }, [sonRaporTarihi]);

  // Kombine dropdown: [R] Reçete + [M] Malzeme (reçetesi olan malzemeler hariç)
  const combinedOptions = useMemo(() => {
    const receteliMalzemeIds = new Set(
      receteler.filter(r => r.urun_malzeme_id).map(r => r.urun_malzeme_id)
    );
    return [
      ...receteler.map(r => ({ value: `recipe:${r.id}`, label: `[R] ${r.ad || r.recete_kodu}${r.tip ? ` (${r.tip})` : ''}` })),
      ...malzemeler.filter(m => !receteliMalzemeIds.has(m.id)).map(m => ({ value: `malzeme:${m.id}`, label: `[M] ${m.ad} (${m.kod})` })),
    ];
  }, [receteler, malzemeler]);

  // Eslesmemis urunler icin fuzzy oneriler yukle
  const loadSuggestions = useCallback(async (unmappedItems) => {
    if (!unmappedItems || unmappedItems.length === 0) { setSuggestions({}); return; }
    const uniqueNames = [...new Set(unmappedItems.map(u => u.urun_adi_pos))].slice(0, 20);
    const results = {};
    const settled = await Promise.allSettled(
      uniqueNames.map(name =>
        fetch(`${API_URL}/api/pos/suggest-mapping?pos_adi=${encodeURIComponent(name)}&limit=3`, { headers: getAuthHeaders() })
          .then(r => r.ok ? r.json() : [])
          .then(data => { results[name] = data; })
      )
    );
    // Merge settled results
    settled.forEach((r, i) => { if (r.status === 'rejected') results[uniqueNames[i]] = []; });
    setSuggestions(results);
  }, []);

  // Gruplandirılmis eslesmemis urunler
  const groupedUnmapped = useMemo(() => {
    if (!selectedRun?.lines) return [];
    const groups = {};
    selectedRun.lines.filter(l => !l.eslesti).forEach(l => {
      const key = l.urun_adi_pos;
      if (!groups[key]) {
        groups[key] = { urun_adi_pos: key, satir_tipi: l.satir_tipi, toplam_miktar: 0, toplam_tutar: 0, count: 0, birim_fiyat: l.birim_fiyat };
      }
      groups[key].toplam_miktar += l.miktar;
      groups[key].toplam_tutar += (l.toplam_tutar || 0);
      groups[key].count += 1;
    });
    return Object.values(groups);
  }, [selectedRun]);

  // Eslesmis satirlar — aynı reçete (veya malzeme) + tip ile gruplanır.
  // Aynı reçeteye eşlenen birden fazla POS adı (örn "İKRAM ÇAY" + "ÇAY") veya
  // aynı POS adının tekrar eden satırları tek satırda toplanır.
  const mappedLines = useMemo(() => {
    if (!selectedRun?.lines) return [];
    const mapped = selectedRun.lines.filter(l => l.eslesti);
    const groups = {};
    mapped.forEach(l => {
      // Group key: recipe_id varsa onu, yoksa malzeme_id, son çare urun_adi_pos
      const targetKey = l.recipe_id ? `r:${l.recipe_id}`
        : l.malzeme_id ? `m:${l.malzeme_id}`
        : `n:${l.urun_adi_pos}`;
      const key = `${targetKey}|${l.satir_tipi}`;
      if (!groups[key]) {
        groups[key] = {
          // İlk satırın temsil bilgileri
          urun_adi_pos: l.urun_adi_pos,
          urun_adi_set: new Set([l.urun_adi_pos]),
          recipe_id: l.recipe_id,
          recipe_ad: l.recipe_ad,
          malzeme_id: l.malzeme_id,
          malzeme_ad: l.malzeme_ad,
          satir_tipi: l.satir_tipi,
          eslesti: true,
          miktar: 0,
          toplam_tutar: 0,
          satir_sayisi: 0,
        };
      } else {
        groups[key].urun_adi_set.add(l.urun_adi_pos);
      }
      groups[key].miktar += (l.miktar || 0);
      groups[key].toplam_tutar += (l.toplam_tutar || 0);
      groups[key].satir_sayisi += 1;
    });
    return Object.values(groups).map(g => {
      // Birim fiyat = toplam tutar / toplam miktar (gösterim)
      const birim_fiyat = g.miktar > 0 ? g.toplam_tutar / g.miktar : null;
      // urun_adi_pos: birden fazla farklı ad varsa "; " ile birleştir
      const adlar = Array.from(g.urun_adi_set);
      const urun_adi_pos = adlar.length === 1 ? adlar[0] : adlar.join(' / ');
      return { ...g, birim_fiyat, urun_adi_pos };
    });
  }, [selectedRun]);

  // Suggestions yukle when run detail acildiginda
  useEffect(() => {
    if (selectedRun?.lines) {
      const unmapped = selectedRun.lines.filter(l => !l.eslesti);
      if (unmapped.length > 0) loadSuggestions(unmapped);
      else setSuggestions({});
    }
  }, [selectedRun, loadSuggestions]);

  useEffect(() => {
    Promise.all([
      fetch(`${API_URL}/api/lokasyonlar`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_URL}/api/recipes?durum=AKTIF`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_URL}/api/pos/runs`, { headers: getAuthHeaders() }).then(r => r.json()),
      fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() }).then(r => r.json()),
    ]).then(([loks, recs, rs, malz]) => {
      setLokasyonlar(Array.isArray(loks) ? loks : []);
      setReceteler(Array.isArray(recs) ? recs : []);
      setRuns(Array.isArray(rs) ? rs : []);
      setMalzemeler(Array.isArray(malz) ? malz : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Sonuç bildirimi: sağ üst köşe toast (sayfa-içi banner kaldırıldı — scroll
  // aşağıdayken görünmüyordu; toast her zaman görünür).
  const showMsg = (msg, type = 'success') => {
    (toast[type] || toast.info)(msg);
  };

  const handleKarakterDuzelt = () => {
    if (!metin.trim()) {
      showMsg('Önce POS raporunu yapıştırınız', 'error');
      return;
    }

    const duzeltilmis = duzeltPosKarakterleri(metin);

    setMetin(duzeltilmis);

    showMsg(
      duzeltilmis === metin
        ? 'Düzeltilecek karakter bulunamadı'
        : 'Türkçe karakterler düzeltildi'
    );
  };

  const handleParse = async () => {
    if (!metin.trim()) { showMsg('Rapor metni giriniz', 'error'); return; }
    if (!lokasyonId) { showMsg('Lokasyon seçiniz', 'error'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/pos/ingest-text`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ metin, lokasyon_id: parseInt(lokasyonId), rapor_tarihi: raporTarihi })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Hata'); }
      const data = await res.json();
      invalidateQueries(['stok', 'dashboard']);
      showMsg(`Rapor yüklendi: ${data.satir_sayisi} satir, ${data.eslesen_satir} eslesti`);
      // Run detayini goster
      const detailRes = await fetch(`${API_URL}/api/pos/runs/${data.run_id}`, { headers: getAuthHeaders() });
      const detail = await detailRes.json();
      setSelectedRun(detail);
      setView('detail');
      setMetin('');
      // Listeyi yenile
      const rs = await fetch(`${API_URL}/api/pos/runs`, { headers: getAuthHeaders() }).then(r => r.json());
      setRuns(Array.isArray(rs) ? rs : []);
    } catch (err) {
      showMsg('Hata: ' + err.message, 'error');
    }
    setSubmitting(false);
  };

  const handleEmailCheck = async () => {
    setEmailChecking(true);
    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);
        const res = await fetch(`${API_URL}/api/pos/email-check`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: '{}',
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (!res.ok) {
          const errText = await res.text();
          let detail = 'Hata';
          try { detail = JSON.parse(errText).detail || detail; } catch (_e) { /* */ }
          throw new Error(detail);
        }
        const data = await res.json();
        if (data.ingested > 0) {
          invalidateQueries(['stok', 'dashboard']);
          showMsg(`${data.ingested} yeni rapor mail ile yüklendi!`);
          const rs = await fetch(`${API_URL}/api/pos/runs`, { headers: getAuthHeaders() }).then(r => r.json());
          setRuns(Array.isArray(rs) ? rs : []);
        } else if (data.checked === 0) {
          const detail = (data.details && data.details[0]) || 'Yeni okunmamis mail yok';
          showMsg(detail, 'info');
        } else {
          showMsg(`${data.checked} mail kontrol edildi, yeni POS raporu bulunamadı`, 'info');
        }
        setEmailChecking(false);
        return;
      } catch (err) {
        const isNetworkErr = err.name === 'TypeError' || err.name === 'AbortError';
        if (isNetworkErr && attempt < maxRetries) {
          await new Promise(r => setTimeout(r, 1500));
          continue;
        }
        const msg = err.name === 'AbortError' ? 'Istek zaman asimina ugradi (30s)' : err.message;
        showMsg('Mail kontrol hatası: ' + msg, 'error');
      }
    }
    setEmailChecking(false);
  };

  const handleIsle = async (runId) => {
    if (!window.confirm('Bu raporu islemek ve stok dusmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URL}/api/pos/runs/${runId}/isle`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' })
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Hata'); }
      const data = await res.json();
      invalidateQueries(['stok', 'dashboard']);
      showMsg(`Islendi: ${data.hareket_sayisi} stok hareketi`);
      if (data.uyarilar && data.uyarilar.length > 0) {
        showMsg(`Islendi (${data.hareket_sayisi} hareket). Uyarilar: ${data.uyarilar.join(', ')}`, 'error');
      }
      // Detayi yenile
      const detail = await fetch(`${API_URL}/api/pos/runs/${runId}`, { headers: getAuthHeaders() }).then(r => r.json());
      setSelectedRun(detail);
      const rs = await fetch(`${API_URL}/api/pos/runs`, { headers: getAuthHeaders() }).then(r => r.json());
      setRuns(Array.isArray(rs) ? rs : []);
    } catch (err) {
      showMsg('Hata: ' + err.message, 'error');
    }
  };

  const handleDelete = async (runId, runNo) => {
    if (!window.confirm(`${runNo} raporunu silmek istediğinize emin misiniz? Bu islem geri alinamaz.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/pos/runs/${runId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Hata'); }
      invalidateQueries(['stok', 'dashboard']);
      showMsg(`${runNo} silindi`);
      setSelectedRun(null);
      setView('list');
      const rs = await fetch(`${API_URL}/api/pos/runs`, { headers: getAuthHeaders() }).then(r => r.json());
      setRuns(Array.isArray(rs) ? rs : []);
    } catch (err) {
      showMsg('Hata: ' + err.message, 'error');
    }
  };

  const handleMapping = async (posUrunAdi, combinedValue) => {
    // combinedValue format: "recipe:123" or "malzeme:456"
    const [type, id] = combinedValue.split(':');
    const body = { pos_urun_adi: posUrunAdi };
    if (type === 'recipe') body.recipe_id = parseInt(id);
    else body.malzeme_id = parseInt(id);

    try {
      const res = await fetch(`${API_URL}/api/pos/mappings`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
      });
      if (!res.ok) throw new Error('Eşleştirme hatası');
      invalidateQueries(['stok', 'dashboard']);
      showMsg('Eşleştirme kaydedildi');
      // Detayi yenile
      if (selectedRun) {
        const detail = await fetch(`${API_URL}/api/pos/runs/${selectedRun.id}`, { headers: getAuthHeaders() }).then(r => r.json());
        setSelectedRun(detail);
      }
    } catch (err) {
      showMsg('Hata: ' + err.message, 'error');
    }
  };

  const lokasyonOptions = lokasyonlar.map(l => ({ value: l.id, label: `${l.ad} (${l.tip})` }));

  if (loading && lokasyonlar.length === 0) return <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>;

  return (
    <div>
      <Header title="POS Satış Verileri" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* KPI Kartlari */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, textAlign: 'center', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: styles.success }}>{sonGunCiro.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} TL</div>
            <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '5px' }}>Son Gun Toplam ({sonGunTarihLabel})</div>
            <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '2px' }}>{sonGunRuns.length} sube</div>
          </div>
          <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, textAlign: 'center', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: styles.info }}>{haftalikCiro.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} TL</div>
            <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '5px' }}>Son 7 Gun Ciro</div>
          </div>
          <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.md, textAlign: 'center', boxShadow: shadows.sm }}>
            <div style={{ fontSize: '28px', fontWeight: 'bold', color: styles.primary }}>{runs.length}</div>
            <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '5px' }}>Toplam Rapor</div>
          </div>
        </div>

        {/* Tab butonlari */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button onClick={() => { setView('list'); setSelectedRun(null); }} style={{ padding: '10px 20px', background: view === 'list' ? styles.secondary : 'white', color: view === 'list' ? 'white' : '#333', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold' }}>Rapor Listesi</button>
          <button onClick={() => setView('form')} style={{ padding: '10px 20px', background: view === 'form' ? styles.primary : 'white', color: view === 'form' ? 'white' : '#333', border: `1px solid ${colors.border}`, borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold' }}>Yeni Rapor Yukle</button>
          {canCheckEmail && (
            <button onClick={handleEmailCheck} disabled={emailChecking} style={{ padding: '10px 20px', background: emailChecking ? '#ccc' : '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', borderRadius: radius.sm, cursor: emailChecking ? 'not-allowed' : 'pointer', fontWeight: 'bold', marginLeft: 'auto' }}>
              {emailChecking ? '⏳ Kontrol ediliyor...' : '📧 Maili Kontrol Et'}
            </button>
          )}
        </div>

        {/* FORM */}
        {view === 'form' && (
          <div style={{ background: colors.surface, padding: '25px', borderRadius: radius.md, boxShadow: shadows.sm }}>
            <h3 style={{ marginTop: 0, color: styles.secondary }}>POS Raporu Yukle</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Lokasyon *</label>
                <SearchableSelect options={lokasyonOptions} value={lokasyonId ? parseInt(lokasyonId) : ''} onChange={v => setLokasyonId(v)} placeholder="Lokasyon seçiniz..." />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>Rapor Tarihi *</label>
                <input type="date" value={raporTarihi} onChange={e => setRaporTarihi(e.target.value)} style={{ width: '100%', padding: '10px', border: '2px solid #e0e0e0', borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '13px' }}>POS Rapor Metni *</label>
              <textarea value={metin} onChange={e => setMetin(e.target.value)} placeholder="POS gun sonu raporunu buraya yapistiriniz..." rows={12} style={{ width: '100%', padding: '12px', border: '2px solid #e0e0e0', borderRadius: radius.sm, fontSize: '13px', fontFamily: 'monospace', resize: 'vertical', boxSizing: 'border-box' }} />
            </div>
            

            <button
              onClick={handleKarakterDuzelt}
              disabled={submitting}
              style={{
                padding: '12px 24px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: radius.sm,
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '15px',
                marginRight: '10px'
              }}
            >
              Karakterleri Düzelt
            </button>
            
            
            <button onClick={handleParse} disabled={submitting} style={{ padding: '12px 30px', background: submitting ? '#ccc' : styles.primary, color: 'white', border: 'none', borderRadius: radius.sm, cursor: submitting ? 'default' : 'pointer', fontWeight: 'bold', fontSize: '15px' }}>
              {submitting ? 'Isleniyor...' : 'Parse Et ve Yukle'}
            </button>
          </div>
        )}

        {/* DETAIL */}
        {view === 'detail' && selectedRun && (
          <div style={{ background: colors.surface, padding: '25px', borderRadius: radius.md, boxShadow: shadows.sm }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: styles.secondary }}>
                  {selectedRun.run_no}
                  {selectedRun.kaynak === 'EMAIL' && <span style={{ marginLeft: '8px', fontSize: '12px', padding: '2px 8px', borderRadius: radius.sm, background: '#e3f2fd', color: '#1565c0' }}>EMAIL</span>}
                </h3>
                <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '4px' }}>
                  {selectedRun.lokasyon_ad} - {selectedRun.rapor_tarihi}
                  {selectedRun.kaynak === 'EMAIL' && selectedRun.email_gonderen && (
                    <span style={{ marginLeft: '10px', color: '#1565c0' }}> | Gonderen: {selectedRun.email_gonderen}</span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <StatusBadge status={selectedRun.durum} />
                {selectedRun.kaynak === 'EMAIL' && selectedRun.durum === 'ISLENDI' && (
                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: radius.sm, background: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold' }}>⚡ Otomatik Islendi</span>
                )}
                {selectedRun.durum === 'YUKLENDI' && selectedRun.eslesmeyen_satir > 0 && (
                  <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: radius.sm, background: '#fff3cd', color: '#856404', fontWeight: 'bold' }}>
                    ⚠ Manuel Islem Gerekli (%{((selectedRun.eslesen_satir / (selectedRun.eslesen_satir + selectedRun.eslesmeyen_satir)) * 100).toFixed(0)} eşleştirme)
                  </span>
                )}
                {selectedRun.durum === 'YUKLENDI' && (
                  <button onClick={() => handleIsle(selectedRun.id)} style={{ padding: '10px 20px', background: styles.success, color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold' }}>Raporu Isle (Stok Dus)</button>
                )}
                {selectedRun.durum !== 'ISLENDI' && (
                  <button onClick={() => handleDelete(selectedRun.id, selectedRun.run_no)} style={{ padding: '10px 20px', background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: radius.sm, cursor: 'pointer', fontWeight: 'bold' }}>Sil</button>
                )}
              </div>
            </div>

            {/* Özet: Brut Ciro, Odenmeyen, Indirim, Net Ciro, Eslesen, Eslesmedi */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '10px', marginBottom: '20px' }}>
              <div style={{ background: colors.backgroundAlt, padding: '12px', borderRadius: radius.sm, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{selectedRun.toplam_ciro?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Brut Ciro</div>
              </div>
              <div style={{ background: '#fff3cd', padding: '12px', borderRadius: radius.sm, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#856404' }}>{(selectedRun.odenmeyen_tutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Odenmeyen</div>
              </div>
              <div style={{ background: '#fff3cd', padding: '12px', borderRadius: radius.sm, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#856404' }}>{(selectedRun.indirim_tutar || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Indirim</div>
              </div>
              <div style={{ background: '#d4edda', padding: '12px', borderRadius: radius.sm, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#155724' }}>{(selectedRun.net_ciro != null ? selectedRun.net_ciro : (selectedRun.toplam_ciro || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Net Ciro</div>
              </div>
              <div style={{ background: '#d4edda', padding: '12px', borderRadius: radius.sm, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#155724' }}>{selectedRun.eslesen_satir}</div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Eslesen</div>
              </div>
              <div style={{ background: selectedRun.eslesmeyen_satir > 0 ? '#f8d7da' : '#f8f9fa', padding: '12px', borderRadius: radius.sm, textAlign: 'center' }}>
                <div style={{ fontWeight: 'bold', fontSize: '18px', color: selectedRun.eslesmeyen_satir > 0 ? '#721c24' : '#333' }}>{selectedRun.eslesmeyen_satir}</div>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Eslesmedi</div>
              </div>
            </div>

            {/* Ödeme Dagilimi — aynı tip (örn. İŞLETME İKRAM) tek satırda toplanır.
                Düzeltme satırları (İNDİRİM/ÖDENMEZ/İKRAM, satir_sinifi != KANAL)
                kanal kartlarına ve % tabanına girmez; ayrı şeritte gösterilir.
                ***<kişi> cari kırılımı (CARI_DETAY) da kanal sayılmaz — tutarı
                ebeveyn CARİ satırında zaten var (çift sayım önlenir). */}
            {selectedRun.odemeler && selectedRun.odemeler.length > 0 && (() => {
              const { kanallar, duzeltmeler, cariDetay } = splitOdemeler(aggregateOdemeler(selectedRun.odemeler));
              const odemeToplam = kanallar.reduce((s, o) => s + o.tutar, 0);
              const cariDetayToplam = cariDetay.reduce((s, o) => s + o.tutar, 0);
              return (
              <div style={{ marginBottom: '20px', background: '#f0f4ff', padding: '15px', borderRadius: radius.md, border: '1px solid #c5cae9' }}>
                <h4 style={{ margin: '0 0 12px 0', color: '#283593' }}>Ödeme Dagilimi</h4>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(Math.max(kanallar.length, 1), 4)}, 1fr)`, gap: '10px' }}>
                  {kanallar.map((p, idx) => {
                    const yuzde = odemeToplam > 0 ? ((p.tutar / odemeToplam) * 100).toFixed(1) : 0;
                    return (
                      <div key={idx} style={{ background: colors.surface, padding: '12px', borderRadius: radius.sm, textAlign: 'center', border: `1px solid ${colors.border}` }}>
                        <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#283593' }}>
                          {p.tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                        </div>
                        <div style={{ fontSize: '12px', color: colors.textSecondary, marginTop: '4px' }}>{p.odeme_tipi}</div>
                        <div style={{ fontSize: '11px', color: colors.textTertiary, marginTop: '2px' }}>%{yuzde}</div>
                      </div>
                    );
                  })}
                </div>
                {duzeltmeler.length > 0 && (
                  <div style={{ marginTop: '12px', background: '#fff8e1', padding: '10px 12px', borderRadius: radius.sm, border: '1px solid #ffe0b2' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#e65100', marginBottom: '6px' }}>
                      Ciro Düzeltmeleri (ödeme kanalı değil — net ciroda düşülür)
                    </div>
                    {duzeltmeler.map((p, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '2px 0', color: '#6d4c41' }}>
                        <span>
                          {p.odeme_tipi}
                          <span style={{ marginLeft: '8px', fontSize: '10px', fontWeight: 'bold', padding: '1px 6px', borderRadius: radius.lg, background: '#ffe0b2', color: '#e65100' }}>
                            {p.satir_sinifi === 'ODENMEYEN' ? 'ÖDENMEZ' : p.satir_sinifi}
                          </span>
                        </span>
                        <span style={{ fontWeight: '600' }}>{p.tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>
                      </div>
                    ))}
                  </div>
                )}
                {cariDetay.length > 0 && (
                  <div style={{ marginTop: '12px', background: '#e8f5e9', padding: '10px 12px', borderRadius: radius.sm, border: '1px solid #c8e6c9' }}>
                    <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#2e7d32', marginBottom: '6px' }}>
                      Cari Detayı — kişi kırılımı ({cariDetayToplam.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL; CARİ kanalında zaten dahil, toplama girmez)
                    </div>
                    {cariDetay.map((p, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '2px 0', color: '#33691e' }}>
                        <span>{p.odeme_tipi.replace(/^\*+/, '')}</span>
                        <span style={{ fontWeight: '600' }}>{p.tutar.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })()}

            {/* Eslesmemis urunler - gruplandirılmis */}
            {groupedUnmapped.length > 0 && (
              <div style={{ marginBottom: '20px', background: '#fff8e1', padding: '15px', borderRadius: radius.md, border: '1px solid #ffe0b2' }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#e65100' }}>Eslesmemis Urunler ({groupedUnmapped.length} farkli urun)</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#fff3cd' }}>
                      <th style={{ padding: '8px', textAlign: 'left' }}>Urun Adi (POS)</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Toplam Adet</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Birim Fiyat</th>
                      <th style={{ padding: '8px', textAlign: 'right' }}>Toplam Tutar</th>
                      <th style={{ padding: '8px', textAlign: 'center' }}>Tip</th>
                      <th style={{ padding: '8px', textAlign: 'left', width: '280px' }}>Eslestir (Reçete/Malzeme)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedUnmapped.map((g, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #ffe0b2' }}>
                        <td style={{ padding: '8px', fontWeight: 'bold' }}>
                          {g.urun_adi_pos}
                          {g.count > 1 && <span style={{ fontSize: '11px', color: colors.textSecondary, marginLeft: '6px' }}>({g.count} satir)</span>}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{g.toplam_miktar}</td>
                        <td style={{ padding: '8px', textAlign: 'right', color: '#1565c0', fontWeight: '600' }}>
                          {g.birim_fiyat != null ? `${g.birim_fiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL` : '-'}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'right' }}>{g.toplam_tutar?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                        <td style={{ padding: '8px', textAlign: 'center' }}>
                          <span style={{
                            padding: '3px 8px', borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold',
                            background: g.satir_tipi === 'SATIS' ? '#d4edda' : g.satir_tipi === 'IPTAL' ? '#f8d7da' : '#e8d4f0',
                            color: g.satir_tipi === 'SATIS' ? '#155724' : g.satir_tipi === 'IPTAL' ? '#721c24' : '#6f42c1'
                          }}>{g.satir_tipi}</span>
                        </td>
                        <td style={{ padding: '8px' }}>
                          <SearchableSelect
                            options={combinedOptions}
                            value=""
                            onChange={(val) => handleMapping(g.urun_adi_pos, val)}
                            placeholder="[R]ecete / [M]alzeme sec..."
                          />
                          {suggestions[g.urun_adi_pos] && suggestions[g.urun_adi_pos].length > 0 && (
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                              {suggestions[g.urun_adi_pos].map((s, i) => (
                                <button key={i}
                                  onClick={() => handleMapping(g.urun_adi_pos, `${s.type === 'recipe' ? 'recipe' : 'malzeme'}:${s.id}`)}
                                  style={{ padding: '3px 8px', fontSize: '11px', border: '1px solid #90caf9', borderRadius: radius.sm, background: '#e3f2fd', color: '#1565c0', cursor: 'pointer' }}
                                  title={`${s.ad} - %${Math.round(s.score * 100)} benzerlik`}
                                >
                                  {s.type === 'recipe' ? '[R]' : '[M]'} {s.ad} ({Math.round(s.score * 100)}%)
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Eslesmis Satirlar */}
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: styles.secondary, color: 'white' }}>
                  <th style={{ padding: '7px', textAlign: 'left' }}>Urun Adi (POS)</th>
                  <th style={{ padding: '7px', textAlign: 'right' }}>Adet</th>
                  <th style={{ padding: '7px', textAlign: 'right' }}>Birim Fiyat</th>
                  <th style={{ padding: '7px', textAlign: 'right' }}>Tutar</th>
                  <th style={{ padding: '7px', textAlign: 'center' }}>Tip</th>
                  <th style={{ padding: '7px', textAlign: 'left' }}>Eşleştirme</th>
                </tr>
              </thead>
              <tbody>
                {mappedLines.length === 0 && groupedUnmapped.length === 0 ? (
                  <tr><td colSpan="6" style={{ padding: '30px', textAlign: 'center', color: colors.textTertiary }}>Satir bulunamadı.</td></tr>
                ) : mappedLines.map((line, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '10px', fontWeight: 'bold' }}>
                      {line.urun_adi_pos}
                      {line.satir_sayisi > 1 && (
                        <span title={`${line.satir_sayisi} ham satır birleştirildi`}
                              style={{ marginLeft: '6px', fontSize: '10px', color: colors.textSecondary, fontWeight: 'normal' }}>
                          ({line.satir_sayisi})
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '7px', textAlign: 'right' }}>{line.miktar}</td>
                    <td style={{ padding: '7px', textAlign: 'right', color: '#1565c0', fontWeight: '600' }}>
                      {line.birim_fiyat != null ? `${line.birim_fiyat.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL` : '-'}
                    </td>
                    <td style={{ padding: '7px', textAlign: 'right' }}>{line.toplam_tutar?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                    <td style={{ padding: '7px', textAlign: 'center' }}>
                      <span style={{
                        padding: '3px 8px', borderRadius: radius.lg, fontSize: '11px', fontWeight: 'bold',
                        background: line.satir_tipi === 'SATIS' ? '#d4edda' : line.satir_tipi === 'IPTAL' ? '#f8d7da' : '#e8d4f0',
                        color: line.satir_tipi === 'SATIS' ? '#155724' : line.satir_tipi === 'IPTAL' ? '#721c24' : '#6f42c1'
                      }}>{line.satir_tipi}</span>
                    </td>
                    <td style={{ padding: '10px' }}>
                      <span style={{ color: styles.success, fontWeight: 'bold' }}>
                        {line.recipe_ad ? `[R] ${line.recipe_ad}` : line.malzeme_ad ? `[M] ${line.malzeme_ad}` : '-'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button onClick={() => { setView('list'); setSelectedRun(null); }} style={{ marginTop: '15px', padding: '10px 20px', background: '#ddd', border: 'none', borderRadius: radius.sm, cursor: 'pointer' }}>Listeye Don</button>
          </div>
        )}

        {/* LIST — lokasyon bazlı son 14 gün */}
        {view === 'list' && (
          <div style={{ background: colors.surface, borderRadius: radius.md, overflow: 'hidden', boxShadow: shadows.sm }}>
            {(() => {
              const grouped = groupRunsByLokasyon(runs);
              const dates = generateLast14Days();
              return (
                <div>
                  {TARGET_LOKASYONLAR.map(lok => (
                    <div key={lok.id} style={{ marginBottom: '20px' }}>
                      <div style={{
                        padding: '10px 16px',
                        background: '#263238',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        letterSpacing: '0.5px',
                      }}>
                        ── {lok.ad.toUpperCase()} ──
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#f5f5f5', color: '#555', fontSize: '12px' }}>
                            <th style={{ padding: '8px', textAlign: 'left', width: '120px' }}>Tarih</th>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Run No</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>Net Ciro</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>Adet</th>
                            <th style={{ padding: '8px', textAlign: 'center' }}>Eşleştirme</th>
                            <th style={{ padding: '8px', textAlign: 'center' }}>Durum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dates.map(tarih => {
                            const run = findRunByDate(grouped[lok.id], tarih);
                            if (!run) {
                              return (
                                <tr key={tarih} style={{ background: '#ffebee', borderBottom: `1px solid ${colors.border}` }}>
                                  <td style={{ padding: '10px', fontWeight: 'bold', color: '#c62828' }}>{tarih}</td>
                                  <td colSpan="5" style={{ padding: '10px', color: '#c62828', fontWeight: 'bold' }}>
                                    ❌ GELMEDI
                                  </td>
                                </tr>
                              );
                            }
                            return (
                              <tr
                                key={tarih}
                                onClick={async () => {
                                  const detail = await fetch(`${API_URL}/api/pos/runs/${run.id}`, { headers: getAuthHeaders() }).then(r => r.json());
                                  setSelectedRun(detail);
                                  setView('detail');
                                }}
                                style={{ borderBottom: `1px solid ${colors.border}`, cursor: 'pointer' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                                onMouseLeave={e => e.currentTarget.style.background = 'white'}
                              >
                                <td style={{ padding: '10px', fontWeight: 'bold' }}>{tarih}</td>
                                <td style={{ padding: '10px' }}>
                                  {run.run_no}
                                  {run.kaynak === 'EMAIL' && <span style={{ marginLeft: '6px', fontSize: '11px', padding: '1px 6px', borderRadius: '3px', background: '#e3f2fd', color: '#1565c0' }}>EMAIL</span>}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                                  {(run.net_ciro != null ? run.net_ciro : (run.toplam_ciro || 0)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{run.toplam_adet}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <span style={{ color: styles.success }}>{run.eslesen_satir}</span>
                                  {run.eslesmeyen_satir > 0 && <span style={{ color: styles.danger }}> / {run.eslesmeyen_satir}</span>}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <StatusBadge status={run.durum} />
                                  {run.kaynak === 'EMAIL' && run.durum === 'ISLENDI' && (
                                    <span style={{ marginLeft: '4px', fontSize: '10px', padding: '2px 6px', borderRadius: '3px', background: '#c8e6c9', color: '#2e7d32', fontWeight: 'bold' }}>⚡ Oto</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default POSSatisPage;
